import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const PLATFORM_ROLES = [
  { value: 'platform_admin', label: 'Platform Admin', color: ['#2c3e50','#fff'], desc: 'Full access + admin dashboard' },
  { value: 'barn_owner',     label: 'Barn Owner',     color: ['#eeedfe','#534AB7'], desc: 'Runs a barn, full management' },
  { value: 'trainer',        label: 'Trainer',        color: ['#fbeaf0','#D4537E'], desc: 'Teaches lessons, scheduling' },
  { value: 'staff',          label: 'Staff',          color: ['#e6f1fb','#185FA5'], desc: 'Clock in, tasks, turnout' },
  { value: 'boarder',        label: 'Boarder',        color: ['#faeeda','#BA7517'], desc: 'Horse owner at a barn' },
  { value: 'member',         label: 'Member',         color: ['#e8f5ee','#2d7a4f'], desc: 'Basic account, no barn yet' },
];

const BARN_ROLES = ['member','trainer','manager','staff','boarder'];
const MEMBERSHIP_STATUSES = ['pending','active','removed'];

const StatCard = ({ label, value, color = 'var(--cr-primary)' }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 120, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
    <div style={{ fontSize: 28, fontWeight: 800, color }}>{value ?? '—'}</div>
    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
  </div>
);

const Badge = ({ label, color = '#eee', text = '#333' }) => (
  <span style={{ background: color, color: text, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{label}</span>
);

export const ImpersonationBanner = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('cr_admin_token');
  const adminUser = (() => { try { return JSON.parse(localStorage.getItem('cr_admin_user')); } catch { return null; } })();
  if (!adminToken || !adminUser) return null;
  const returnToAdmin = () => {
    localStorage.setItem('cr_token', adminToken);
    localStorage.setItem('cr_user', JSON.stringify(adminUser));
    localStorage.removeItem('cr_admin_token');
    localStorage.removeItem('cr_admin_user');
    updateUser(adminUser);
    navigate('/admin');
  };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#c0392b', color: '#fff', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 600 }}>
      <span>👁 Viewing as {user?.first_name} {user?.last_name} ({user?.email})</span>
      <button onClick={returnToAdmin} style={{ background: '#fff', color: '#c0392b', border: 'none', borderRadius: 8, padding: '4px 12px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>← Return to admin</button>
    </div>
  );
};

// ── Role checkboxes for a user ────────────────────────────────────────────────
function RoleCheckboxes({ userId, currentRoles, isSuspended, isRevoked, onUpdate }) {
  const roles = currentRoles ? currentRoles.split(',').map(r => r.trim()).filter(Boolean) : [];

  const toggleRole = (role) => {
    let updated;
    if (roles.includes(role)) {
      updated = roles.filter(r => r !== role);
      if (updated.length === 0) updated = ['member'];
    } else {
      updated = [...roles.filter(r => r !== 'member'), role];
      if (updated.length === 0) updated = ['member'];
    }
    onUpdate(userId, { roles: updated.join(',') });
  };

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Platform Roles</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {PLATFORM_ROLES.map(r => {
          const active = roles.includes(r.value);
          const [bg, fg] = r.color;
          return (
            <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, border: `2px solid ${active ? fg : 'var(--border-color)'}`, background: active ? bg : '#fff' }}>
              <input type="checkbox" checked={active} onChange={() => toggleRole(r.value)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: active ? fg : 'var(--text-primary)' }}>{r.label}</div>
                <div style={{ fontSize: 11, color: active ? fg : 'var(--text-muted)' }}>{r.desc}</div>
              </div>
            </label>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Account Status</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, border: `2px solid ${isSuspended ? 'var(--cr-amber)' : 'var(--border-color)'}`, background: isSuspended ? 'var(--cr-amber-light)' : '#fff', fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={isSuspended} onChange={e => onUpdate(userId, { is_suspended: e.target.checked })} style={{ cursor: 'pointer' }} />
          ⏸ Suspended
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, border: `2px solid ${isRevoked ? 'var(--cr-red)' : 'var(--border-color)'}`, background: isRevoked ? 'var(--cr-red-light)' : '#fff', fontSize: 13, fontWeight: 600, color: isRevoked ? 'var(--cr-red)' : 'var(--text-primary)' }}>
          <input type="checkbox" checked={isRevoked} onChange={e => onUpdate(userId, { is_revoked: e.target.checked })} style={{ cursor: 'pointer' }} />
          🚫 Revoked
        </label>
      </div>
    </div>
  );
}

// ── User memberships panel ────────────────────────────────────────────────────
function UserMemberships({ userId }) {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/users/${userId}/memberships`); setMemberships(data || []); }
    catch {} finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const update = async (membershipId, updates) => {
    setSaving(membershipId);
    try {
      await api.put(`/admin/users/${userId}/memberships/${membershipId}`, updates);
      setMsg('✓ Updated'); setTimeout(() => setMsg(''), 2000);
      await load();
    } catch { setMsg('Failed'); } finally { setSaving(null); }
  };

  if (loading) return <div style={{ padding: '8px 0', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>;
  if (memberships.length === 0) return <div style={{ padding: '8px 0', color: 'var(--text-muted)', fontSize: 13 }}>Not a member of any barn.</div>;

  const roleColor = (r) => ({ manager: ['#eeedfe','#534AB7'], staff: ['#e6f1fb','#185FA5'], trainer: ['#fbeaf0','#D4537E'], boarder: ['#faeeda','#BA7517'], member: ['#e8f5ee','#2d7a4f'] }[r] || ['#eee','#333']);
  const statusColor = (s) => ({ active: ['var(--cr-green-light)','var(--cr-green-dark)'], pending: ['var(--cr-amber-light)','var(--cr-amber)'], removed: ['var(--cr-red-light)','var(--cr-red)'] }[s] || ['#eee','#333']);

  return (
    <div style={{ marginTop: 8 }}>
      {msg && <div style={{ fontSize: 12, color: 'var(--cr-green-dark)', marginBottom: 6 }}>{msg}</div>}
      {memberships.map(m => {
        const [roleBg, roleFg] = roleColor(m.role);
        const [stBg, stFg] = statusColor(m.status);
        return (
          <div key={m.id} style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: '12px', marginBottom: 8, border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{m.Organization?.name || 'Unknown barn'}</div>
                {m.Organization?.location && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{m.Organization.location}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Badge label={m.role} color={roleBg} text={roleFg} />
                <Badge label={m.status} color={stBg} text={stFg} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>BARN ROLE</label>
                <select value={m.role} disabled={saving === m.id} onChange={e => update(m.id, { role: e.target.value })}
                  style={{ borderRadius: 8, border: '1.5px solid var(--border-color)', padding: '5px 10px', fontSize: 13 }}>
                  {BARN_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>STATUS</label>
                <select value={m.status} disabled={saving === m.id} onChange={e => update(m.id, { status: e.target.value })}
                  style={{ borderRadius: 8, border: '1.5px solid var(--border-color)', padding: '5px 10px', fontSize: 13 }}>
                  {MEMBERSHIP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>EMPLOYMENT</label>
                <select value={m.employment_type || 'employee'} disabled={saving === m.id} onChange={e => update(m.id, { employment_type: e.target.value })}
                  style={{ borderRadius: 8, border: '1.5px solid var(--border-color)', padding: '5px 10px', fontSize: 13 }}>
                  <option value="employee">Employee</option>
                  <option value="contractor">1099 Contractor</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>JOB TITLE</label>
              <input defaultValue={m.job_title || ''} placeholder="e.g. Stable Hand"
                onBlur={e => { if (e.target.value !== (m.job_title || '')) update(m.id, { job_title: e.target.value }); }}
                style={{ borderRadius: 8, border: '1.5px solid var(--border-color)', padding: '5px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 6 }}>PERMISSIONS</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[['can_complete_tasks','Complete tasks'],['can_assign_tasks','Assign tasks'],['can_view_feed_plans','View feed plans'],['can_view_horses','View horses'],['can_schedule','Schedule'],['can_add_notes','Add notes'],['can_send_documents','Send docs'],['can_send_invites','Send invites'],['can_message','Message']].map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', background: m[key] ? 'var(--cr-green-light)' : 'var(--bg-tertiary)', color: m[key] ? 'var(--cr-green-dark)' : 'var(--text-secondary)', borderRadius: 6, padding: '4px 8px' }}>
                    <input type="checkbox" checked={!!m[key]} disabled={saving === m.id} onChange={e => update(m.id, { [key]: e.target.checked })} style={{ cursor: 'pointer' }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main admin page ────────────────────────────────────────────────────────────
const AdminPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab]           = useState('overview');
  const [stats, setStats]       = useState(null);
  const [users, setUsers]       = useState([]);
  const [orgs, setOrgs]         = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState('');
  const [expanded, setExpanded] = useState(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get('/admin/dashboard'); setStats(data); } catch {}
  }, []);

  const loadUsers = useCallback(async (q = '') => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ''}`); setUsers(data); }
    catch {} finally { setLoading(false); }
  }, []);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/orgs'); setOrgs(data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); loadUsers(); }, [loadStats, loadUsers]);
  useEffect(() => {
    if (tab === 'users') loadUsers();
    if (tab === 'barns') loadOrgs();
  }, [tab, loadUsers, loadOrgs]);

  const impersonate = async (u) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${u.id}`);
      localStorage.setItem('cr_admin_token', localStorage.getItem('cr_token'));
      localStorage.setItem('cr_admin_user', localStorage.getItem('cr_user'));
      localStorage.setItem('cr_token', data.token);
      localStorage.setItem('cr_user', JSON.stringify(data.user));
      updateUser(data.user);
      const r = String((data.user && (data.user.roles || data.user.role)) || '');
      if (r.includes('platform_admin')) { setMsg('You are the platform admin - already viewing as yourself'); setTimeout(() => setMsg(''), 3000); }
      else navigate('/');
    } catch (e) { setMsg(e.response?.data?.message || 'Could not impersonate'); }
  };

  const updateUserField = async (id, updates) => {
    try {
      await api.put(`/admin/users/${id}`, updates);
      setMsg('✓ Updated');
      loadUsers(search);
      setTimeout(() => setMsg(''), 2000);
    } catch { setMsg('Failed'); }
  };

  const TabBtn = ({ name, label }) => (
    <button onClick={() => setTab(name)} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: tab === name ? 'var(--cr-primary)' : 'var(--bg-tertiary)', color: tab === name ? '#fff' : 'var(--text-secondary)' }}>{label}</button>
  );

  const getRoleLabels = (roles) => {
    if (!roles) return [];
    return roles.split(',').map(r => r.trim()).filter(Boolean).map(r => PLATFORM_ROLES.find(p => p.value === r) || { value: r, label: r, color: ['#eee','#333'] });
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 80 }}>
      <div style={{ background: '#2c3e50', color: '#fff', padding: '20px 16px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>⚙️ Admin Dashboard</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>Connected Rider · {user?.email}</div>
          </div>
          <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>← App</button>
        </div>
        {msg && <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 12px', fontSize: 13 }}>{msg}</div>}
      </div>

      {/* Role preview panel */}
      <div style={{ background: '#1a252f', padding: '12px 16px' }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Preview as role</div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {PLATFORM_ROLES.map(r => (
            <button key={r.value} onClick={async () => {
              try {
                const { data } = await api.get(`/admin/preview-user/${r.value}`);
                impersonate(data.user);
              } catch (e) {
                setMsg(e.response?.data?.message || `No ${r.label} user found`);
              }
            }} style={{ background: r.color[0], color: r.color[1], border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              👁 {r.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '14px 16px', overflowX: 'auto' }}>
        <TabBtn name="overview" label="📊 Overview" />
        <TabBtn name="users"    label="👤 Users" />
        <TabBtn name="barns"    label="🏠 Barns" />
        <button onClick={() => navigate('/admin/beta')} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>🏇 Beta testers</button>
        <button onClick={() => navigate('/admin/feedback')} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>⚑ Feedback</button>
      </div>

      <div style={{ padding: '0 16px' }}>

        {tab === 'overview' && (
          <div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <StatCard label="Active users"  value={stats?.totalUsers}    color="var(--cr-primary)" />
              <StatCard label="Horses"        value={stats?.totalHorses}   color="var(--cr-green)" />
              <StatCard label="Barns"         value={stats?.totalBarns}    color="var(--cr-purple)" />
              <StatCard label="Signups (30d)" value={stats?.recentSignups} color="var(--cr-blue)" />
            </div>
            {stats?.topSearches?.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Top shop searches</div>
                {stats.topSearches.slice(0, 10).map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border-color)', fontSize: 13 }}>
                    <span>{s.search_term}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{s.count} searches · {s.not_found_count} not found</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'users' && (
          <div>
            {loading && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Loading…</div>}
            {(() => { const pending = users.filter(x => x.is_active === false && !x.is_revoked); return pending.length ? (
              <div style={{ background: '#FFF8E8', border: '1px solid #E8D5A0', borderRadius: 12, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>⏳ Pending approval ({pending.length})</div>
              </div>
            ) : null; })()}
            {[...users].sort((a, b) => ((a.is_active === false && !a.is_revoked) ? 0 : 1) - ((b.is_active === false && !b.is_revoked) ? 0 : 1)).map(u => {
              const isExpanded = expanded === u.id;
              const roleLabels = getRoleLabels(u.roles);
              return (
                <div key={u.id} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{u.first_name} {u.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{u.email}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Joined {fmt(u.created_at)}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      {(u.barn_memberships || []).map((bm, i) => {
                        const rc = { manager: ['#eeedfe','#534AB7'], trainer: ['#fbeaf0','#D4537E'], staff: ['#e6f1fb','#185FA5'], boarder: ['#faeeda','#BA7517'], member: ['#e8f5ee','#2d7a4f'] }[bm.role] || ['#eee','#333'];
                        const label = { manager: 'Barn Owner', trainer: 'Trainer', staff: 'Staff', boarder: 'Boarder', member: 'Member' }[bm.role] || bm.role;
                        return <Badge key={i} label={`${bm.barn} \u00b7 ${label}${bm.role === 'trainer' && !bm.offers_lessons ? ' (booking off)' : ''}`} color={rc[0]} text={rc[1]} />;
                      })}
                      {(u.barn_memberships || []).length === 0 && <Badge label="No barn" color="#eee" text="#666" />}
                      {String(u.roles || '').includes('platform_admin') && <Badge label="Platform Admin" color="#2c3e50" text="#fff" />}
                      {u.is_suspended && <Badge label="Suspended" color="var(--cr-amber-light)" text="var(--cr-amber)" />}
                      {u.is_revoked && <Badge label="Revoked" color="var(--cr-red-light)" text="var(--cr-red)" />}
                      {!u.email_verified && <Badge label="Unverified" color="var(--cr-amber-light)" text="var(--cr-amber)" />}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <button onClick={() => impersonate(u)} style={{ background: 'var(--cr-primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>👁 View as</button>
                    {u.is_active === false && !u.is_revoked && <button onClick={() => updateUserField(u.id, { is_active: true })} style={{ background: '#1D6B3F', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6 }}>Approve</button>}
                    {u.is_active === false && !u.is_revoked && <button onClick={() => { if (window.confirm('Deny this signup? They will not be admitted.')) updateUserField(u.id, { is_revoked: true }); }} style={{ background: '#8C2B22', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6 }}>Deny</button>}
                    {!u.email_verified && (
                      <button onClick={() => updateUserField(u.id, { email_verified: true })} style={{ background: 'var(--cr-green-light)', color: 'var(--cr-green-dark)', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✓ Verify email</button>
                    )}
                    <button onClick={() => updateUserField(u.id, { is_active: !u.is_active })} style={{ background: u.is_active ? 'var(--cr-red-light)' : 'var(--cr-green-light)', color: u.is_active ? 'var(--cr-red)' : 'var(--cr-green-dark)', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {u.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                    <button onClick={() => setExpanded(isExpanded ? null : u.id)} style={{ background: isExpanded ? 'var(--cr-primary-light)' : 'var(--bg-tertiary)', color: isExpanded ? 'var(--cr-primary)' : 'var(--text-secondary)', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {isExpanded ? '▲ Less' : '▼ Edit roles & barns'}
                    </button>
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-color)' }}>
                      <RoleCheckboxes
                        userId={u.id}
                        currentRoles={u.roles}
                        isSuspended={!!u.is_suspended}
                        isRevoked={!!u.is_revoked}
                        onUpdate={updateUserField}
                      />
                      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Barn memberships</div>
                        <UserMemberships userId={u.id} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'barns' && (
          <div>
            {loading && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Loading…</div>}
            {orgs.map(o => (
              <div key={o.id} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{o.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{o.location || 'No location'} · {o.org_type}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{o.member_count} active member{o.member_count !== 1 ? 's' : ''} · Created {fmt(o.created_at)}</div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminPage;
